import React, { useContext, useState } from 'react';
import {
  Flex,
  Box,
  theme,
  Modal,
  Text,
  Button,
  Link,
  Flow,
} from '@xometry/xometry_loft';
import { InviteModalData } from '.';
import { useMutation } from '@apollo/client';
import {
  SendOrganizationInvitationsMutationResult,
  SendOrganizationInvitationsMutationVariables,
  SendOrganizationInvitationsQuery,
} from '../../schema/generated/schema';
import NotificationContext from '../../contexts/Notification';

export interface Props {
  invitationModalData: InviteModalData;
  setInvitationModalData: (arg0: any) => void;
}

const InvitationModal = ({
  invitationModalData,
  setInvitationModalData,
}: Props) => {
  const [isResending, setIsResending] = useState<boolean>(false);
  const { addNotification } = useContext(NotificationContext);
  const [sendOrganizationInvitationsMutation] = useMutation<
    SendOrganizationInvitationsMutationResult,
    SendOrganizationInvitationsMutationVariables
  >(SendOrganizationInvitationsQuery);

  const handleModalClose = () => {
    setInvitationModalData({
      ...invitationModalData,
      openStatus: false,
    });
    document.getElementById('invitationModal')?.close();
  };

  return (
    <Modal
      id="invitationModal"
      isOpen={invitationModalData.openStatus}
      breakpointMaxWidth="634px"
      showClose
      onClose={() =>
        setInvitationModalData({
          ...invitationModalData,
          openStatus: false,
        })
      }
      title={
        invitationModalData.isStaffActingOnBehalfOfUser ? (
          <Text kind="panel-title" display="block">
            Unable to {invitationModalData.buttonDecision} Team Invitation
          </Text>
        ) : (
          <Text kind="panel-title" display="block">
            {invitationModalData.buttonDecision} invitation to join{' '}
            {invitationModalData.teamName}?
          </Text>
        )
      }
    >
      {!invitationModalData.isStaffActingOnBehalfOfUser && (
        <>
          <Box marginY={theme.space[4]}>
            {invitationModalData.buttonDecision === 'Accept' && (
              <Text>
                Accepting the invitation from{' '}
                {invitationModalData.inviterFirstName}{' '}
                {invitationModalData.inviterLastName} will give you access to{' '}
                {invitationModalData.teamName}’s dashboard, and allow you to
                share your quotes and orders with the team.
              </Text>
            )}
            {invitationModalData.buttonDecision === 'Decline' && (
              <Text>
                Declining the invitation from{' '}
                {invitationModalData.inviterFirstName}{' '}
                {invitationModalData.inviterLastName} to join{' '}
                {invitationModalData.teamName} will prevent you from sharing
                quotes and orders to this team’s dashboard.
              </Text>
            )}
          </Box>
          <Flex justifyContent="flex-end">
            <Button
              kind={theme.kind.primaryOutline}
              marginRight={theme.space[2]}
              onClick={() => handleModalClose()}
            >
              Cancel
            </Button>
            <Link href={invitationModalData.url}>
              <Button kind={theme.kind.primary}>
                {invitationModalData.buttonDecision} Invitation
              </Button>
            </Link>
          </Flex>
        </>
      )}
      {invitationModalData.isStaffActingOnBehalfOfUser && (
        <>
          <Text kind="body" display="block" marginY={theme.space[4]}>
            Staff is not able to accept or decline an invitation on behalf of a
            user. The {invitationModalData.userName} will need to login to their
            Xometry account or check their email to accept the invitation.
          </Text>
          <Flex justifyContent="flex-end">
            <Button
              kind={theme.kind.primaryOutline}
              marginRight={theme.space[2]}
              onClick={() => handleModalClose()}
            >
              Cancel
            </Button>
            {invitationModalData.buttonDecision === 'Accept' && (
              <Button
                kind={theme.kind.primary}
                onClick={() => {
                  setIsResending(true);
                  sendOrganizationInvitationsMutation({
                    variables: {
                      addUsersInput: {
                        organizationId: invitationModalData.orgId,
                        emails: [invitationModalData.userEmail],
                        permissionIds: [],
                      },
                    },
                    onCompleted: () => {
                      setIsResending(false);
                      addNotification('success', '1 Invitation sent!');
                      handleModalClose();
                    },
                    onError: (error) => {
                      setIsResending(false);
                      console.error(error);
                      addNotification(
                        'danger',
                        'There was an error sending invitations.'
                      );
                      handleModalClose();
                    },
                  });
                }}
              >
                {isResending ? (
                  <Flow kind="disabled" />
                ) : (
                  'Resend Team Invitation'
                )}
              </Button>
            )}
          </Flex>
        </>
      )}
    </Modal>
  );
};

export default InvitationModal;
